import React from 'react'
import { Link } from 'gatsby'
import { Layout, Header, SectionTitle, Intro } from '../../../../components'

import data from './data.json'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      />
      <Layout
        category="Overview"
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
        complete={true}
      >
        <Intro>
          <p>
            Status communicates necessary contextual, point-in-time information
            in relation to an object such as an account, transaction or deal. An
            object's status can change independent of a user's actions.
          </p>
          <p>
            Status relates specifically to an object at a specific point in
            time. If you need to communicate information related to a user's
            task, consider the{' '}
            <Link
              className="link"
              to={'/' + data.site + '/guidelines/feedback-errors/design'}
            >
              feedback and error
            </Link>{' '}
            guidelines. If you need to communicate progression through a task,
            consider the{' '}
            <Link
              className="link"
              to={'/' + data.site + '/guidelines/loading/design'}
            >
              progress and loading
            </Link>{' '}
            guidelines.
          </p>
        </Intro>
        <SectionTitle>Visual Treatment</SectionTitle>
        <p>
          Status is intended to communicate necessary information with the user,
          do not use it for decoration. Because it's inherently connected to a
          singular object, status must be displayed on the UI element that
          represents that object. The visual treatmentn of status should be
          consistent on a given object. That is, if you use a badge to
          communicate a pending status, do not use an icon to indicate a
          complete status.
        </p>

        <h3>Color</h3>
        <p>
          Status should use semantic colors to draw the user's eye and give them
          an immediate understanding of the importance of the information.
        </p>
        <table>
          <th>Color</th>
          <th>Category</th>
          <th>Example Usage</th>
          <tr>
            <td>Gray</td>
            <td>Inactive</td>
            <td>Not started, Disabled, Draft, Scheduled, Removed</td>
          </tr>
          <tr>
            <td>Green</td>
            <td>Positive</td>
            <td>Active, High, Sent, Posted, Up-to-date, Published</td>
          </tr>
          <tr>
            <td>Yellow</td>
            <td>Processing</td>
            <td>Pending, Scheduled, In progress, In review</td>
          </tr>
          <tr>
            <td>Red</td>
            <td>Negative</td>
            <td>In danger, Low, Failed, Canceled, Stuck, Removed</td>
          </tr>
        </table>
        <p>
          Use your discretion in choosing when to use color to make statuses
          more prominent in the visual hierarchy. For example, it may be
          important to highlight posted transactions in green and failed
          transactions in red, but less important to highlight pending
          transactions. In this case, consider using the inactive gray treatment
          for pending instead of the processing yellow treatment.
        </p>

        <h3 class="l-margin-top--xxlarge">Iconography</h3>
        <p>
          Iocns may be used to add visual prominence to a status message. Per
          the{' '}
          <Link
            className="link"
            to={'/' + data.site + '/guidelines/iconography/design'}
          >
            iconography
          </Link>{' '}
          guidelines, icons should reinforce the label and be easily understood.
          Do not use icons that have different connotations elsewhere in the
          experience to signify status. For example, if the{' '}
          <i class="icon icon--search" /> is used in the experience to mean
          'search', do not use it on a status to mean under review.
        </p>
        <hr />

        <SectionTitle>Writing Status Messages</SectionTitle>
        <p>
          Status should always be associated with words, do not rely on semantic
          colors or icons alone. The language that is used to convey status
          should be concise and consistent within an experience. Ideally,
          statuses should be kept to one or two words. Full sentences should
          have simple structures along the lines of "This object is this
          adjective". For example, "This package is in review" or "This account
          is past due".
        </p>
      </Layout>
    </>
  )
}
